import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import classNames from 'classnames';

import ScreenReaderOnly from '../ScreenReaderOnly';

import styles from './_index.module.scss';

import ChatInput from './components/ChatInput';
import ChatMessageItem from './components/ChatMessageItem';
import ResponseProgress from './components/ResponseProgress';

import {
  COMPLETED_RESPONSE_REVEAL_INTERVAL_MS,
  RESPONSE_PROGRESS_FADE_DURATION_MS,
  RESPONSE_PROGRESS_MAX_DELAY_MS,
  RESPONSE_PROGRESS_MESSAGES,
  RESPONSE_PROGRESS_MIN_DELAY_MS,
  SCROLL_POSITION_TOLERANCE_PX,
  SEGMENT_DELAY_MS,
  USER_MESSAGE_BOTTOM_OFFSET_PX,
} from './utilities/constants';
import {
  getNextRevealCount,
  shuffleResponseProgressMessages,
  splitAssistantMessageSegments,
} from './utilities/helpers';
import type { ChatMessage, ChatRole, ChatWindowMarkdownMode } from './utilities/types';

export type {
  ChatMessage,
  ChatMessageVariant,
  ChatRole,
  ChatWindowMarkdownMode,
} from './utilities/types';

export interface ChatWindowProps {
  autoScroll?: boolean;
  className?: string;
  footerHint?: ReactNode;
  headerAction?: ReactNode;
  headerTitle?: string;
  inputDisabled?: boolean;
  inputErrorMessageId?: string;
  inputFooter?: ReactNode;
  inputHasError?: boolean;
  inputValue?: string;
  isAgentStreaming?: boolean;
  messages?: ChatMessage[];
  onChangeInput?: (value: string) => void;
  onSubmit?: (value: string) => void;
  renderMarkdown?: ChatWindowMarkdownMode;
  renderMessage?: (message: ChatMessage) => ReactNode | undefined;
  submitDisabled?: boolean;
}

type SegmentTimeoutId = ReturnType<typeof setTimeout>;

const ChatWindow = ({
  autoScroll = true,
  className,
  footerHint,
  headerAction,
  headerTitle,
  inputDisabled = false,
  inputErrorMessageId,
  inputFooter,
  inputHasError = false,
  inputValue = '',
  isAgentStreaming = false,
  messages = [],
  onChangeInput,
  onSubmit,
  renderMarkdown = 'assistant',
  renderMessage,
  submitDisabled = false,
}: ChatWindowProps) => {
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [pendingInput, setPendingInput] = useState<null | string>(null);
  const [responseProgressMessage, setResponseProgressMessage] = useState(
    RESPONSE_PROGRESS_MESSAGES[0]
  );
  const [responseReservationHeight, setResponseReservationHeight] = useState(0);
  const [responseReadyAnnouncement, setResponseReadyAnnouncement] = useState('');
  const [isResponseProgressFading, setIsResponseProgressFading] = useState(false);
  const [visibleSegmentCounts, setVisibleSegmentCounts] = useState<Record<string, number>>({});
  const [visibleCharacterCounts, setVisibleCharacterCounts] = useState<Record<string, number>>({});

  const generatedMessageCounter = useRef(0);
  const anchorScrollTopRef = useRef<null | number>(null);
  const hasResponseReservationRef = useRef(false);
  const lastAnchoredUserMessageIdRef = useRef<null | string>(null);
  const messageBubbleRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingScrollAnchorIdRef = useRef<null | string>(null);
  const previousCombinedMessageIdsRef = useRef<null | string[]>(null);
  const previousRemoteMessageIdsRef = useRef<string[]>([]);
  const progressivelyRevealedMessageIdsRef = useRef<Set<string>>(new Set());
  const announcedResponseMessageIdsRef = useRef<Set<string>>(new Set());
  const remoteMessageIdsAtSubmitRef = useRef<Set<string>>(new Set());
  const responseReservationRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasUserMovedViewportRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const programmaticScrollTopRef = useRef<null | number>(null);
  const segmentCacheRef = useRef<Record<string, string[]>>({});
  const segmentTimeoutsRef = useRef<Record<string, SegmentTimeoutId[]>>({});
  const characterRevealTimeoutsRef = useRef<Record<string, SegmentTimeoutId>>({});
  const visibleSegmentCountsRef = useRef<Record<string, number>>({});
  const responseProgressLastMessageRef = useRef<null | string>(null);
  const responseProgressQueueRef = useRef<string[]>([]);
  const responseProgressTimeoutsRef = useRef<SegmentTimeoutId[]>([]);
  const responseReadyAnnouncementTimeoutRef = useRef<null | SegmentTimeoutId>(null);

  const scrollChatBodyTo = (container: HTMLDivElement, nextScrollTop: number) => {
    container.scrollTop = nextScrollTop;
    lastScrollTopRef.current = container.scrollTop;
    programmaticScrollTopRef.current = container.scrollTop;
  };

  const announceResponseReady = () => {
    if (responseReadyAnnouncementTimeoutRef.current) {
      clearTimeout(responseReadyAnnouncementTimeoutRef.current);
    }

    setResponseReadyAnnouncement('');
    responseReadyAnnouncementTimeoutRef.current = setTimeout(() => {
      setResponseReadyAnnouncement('Nexa’s response is ready.');
      responseReadyAnnouncementTimeoutRef.current = null;
    }, 0);
  };

  const visibleLocalMessages = useMemo(
    () =>
      localMessages.filter((localMessage) => {
        return !messages.some(
          (remoteMessage) =>
            !remoteMessageIdsAtSubmitRef.current.has(remoteMessage.id) &&
            remoteMessage.role === 'user' &&
            remoteMessage.content === localMessage.content
        );
      }),
    [localMessages, messages]
  );
  const combinedMessages = useMemo(
    () => [...messages, ...visibleLocalMessages],
    [messages, visibleLocalMessages]
  );
  const customMessageContentById = useMemo(() => {
    const contentById = new Map<string, ReactNode>();
    if (!renderMessage) return contentById;

    combinedMessages.forEach((message) => {
      const customContent = renderMessage(message);
      if (customContent !== undefined) {
        contentById.set(message.id, customContent);
      }
    });

    return contentById;
  }, [combinedMessages, renderMessage]);

  useEffect(() => {
    visibleSegmentCountsRef.current = visibleSegmentCounts;
  }, [visibleSegmentCounts]);

  useLayoutEffect(() => {
    const nextMessageIds = messages.map((message) => message.id);
    const previousMessageIds = previousRemoteMessageIdsRef.current;
    const previousMessageIdSet = new Set(previousMessageIds);
    const remoteMessagesChanged =
      nextMessageIds.length !== previousMessageIds.length ||
      nextMessageIds.some((id, index) => id !== previousMessageIds[index]);

    previousRemoteMessageIdsRef.current = nextMessageIds;

    const messageToReveal =
      previousMessageIds.length > 0
        ? [...messages]
            .reverse()
            .find(
              (message) =>
                message.shouldSmoothScrollIntoView && !previousMessageIdSet.has(message.id)
            )
        : undefined;

    if (autoScroll && messageToReveal) {
      const container = scrollContainerRef.current;
      const messageBubble = messageBubbleRefs.current[messageToReveal.id];

      if (container && messageBubble) {
        const containerRect = container.getBoundingClientRect();
        const messageRect = messageBubble.getBoundingClientRect();
        const messageBottomInScrollContent =
          messageRect.bottom - containerRect.top + container.scrollTop;

        anchorScrollTopRef.current = null;
        hasResponseReservationRef.current = false;
        pendingScrollAnchorIdRef.current = null;
        setResponseReservationHeight(0);
        container.scrollTo({
          behavior: 'smooth',
          top: Math.max(0, messageBottomInScrollContent - container.clientHeight + 20),
        });
        return;
      }
    }

    const hasActiveTurnAnchor =
      anchorScrollTopRef.current !== null || pendingScrollAnchorIdRef.current !== null;
    if (
      !autoScroll ||
      !remoteMessagesChanged ||
      !nextMessageIds.length ||
      hasActiveTurnAnchor ||
      isAgentStreaming
    ) {
      return;
    }

    const container = scrollContainerRef.current;
    if (container) {
      scrollChatBodyTo(container, container.scrollHeight);
    }
  }, [autoScroll, isAgentStreaming, messages]);

  const assistantMessageCount = useMemo(
    () => messages.filter((message) => message.role === 'assistant').length,
    [messages]
  );

  const assistantCountSnapshotRef = useRef<number>(assistantMessageCount);
  const isAgentStreamingSnapshotRef = useRef<boolean>(isAgentStreaming);

  const hasStreamingAssistantMessage = combinedMessages.some(
    (message) => message.role === 'assistant' && message.isStreaming
  );
  const hasCompletedResponseRevealInProgress = combinedMessages.some((message) => {
    if (
      message.role !== 'assistant' ||
      message.isStreaming ||
      customMessageContentById.has(message.id) ||
      typeof message.content !== 'string' ||
      !message.content.trim()
    ) {
      return false;
    }

    const visibleCharacterCount = visibleCharacterCounts[message.id];
    if (typeof visibleCharacterCount === 'number') {
      return visibleCharacterCount < message.content.length;
    }

    return (
      anchorScrollTopRef.current !== null &&
      Boolean(message.shouldReveal) &&
      !progressivelyRevealedMessageIdsRef.current.has(message.id)
    );
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const assistantMessages = combinedMessages.filter(
      (message) =>
        message.role === 'assistant' &&
        !customMessageContentById.has(message.id) &&
        typeof message.content === 'string' &&
        message.content.trim()
    );

    const activeIds = new Set<string>();
    const nextVisibleCounts: Record<string, number> = {};

    assistantMessages.forEach((message) => {
      const contentString = message.content as string;
      const segments = splitAssistantMessageSegments(contentString);
      const totalSegments = segments.length;
      const isStreamingMessage = Boolean(message.isStreaming);
      const existingVisibleCount = visibleSegmentCountsRef.current[message.id];

      segmentCacheRef.current[message.id] = segments;
      activeIds.add(message.id);

      const nextVisibleCount = isStreamingMessage
        ? (existingVisibleCount ?? 1)
        : Math.max(existingVisibleCount ?? 0, totalSegments);
      nextVisibleCounts[message.id] = nextVisibleCount;

      if (!isStreamingMessage) {
        const existingTimers = segmentTimeoutsRef.current[message.id];
        if (existingTimers) {
          existingTimers.forEach((timeoutId) => clearTimeout(timeoutId));
          delete segmentTimeoutsRef.current[message.id];
        }
        return;
      }

      const currentVisibleCount = nextVisibleCounts[message.id] ?? 1;
      const existingTimerCount = segmentTimeoutsRef.current[message.id]?.length ?? 0;
      const targetTimerCount = Math.max(totalSegments - currentVisibleCount, 0);
      const timersToAdd = targetTimerCount - existingTimerCount;

      if (timersToAdd > 0) {
        const newTimers = Array.from({ length: timersToAdd }, (_, index) =>
          setTimeout(
            () => {
              setVisibleSegmentCounts((previous) => {
                const currentCount = previous[message.id] ?? 1;
                if (currentCount >= totalSegments) return previous;

                return {
                  ...previous,
                  [message.id]: currentCount + 1,
                };
              });
            },
            SEGMENT_DELAY_MS * (existingTimerCount + index + 1)
          )
        );

        segmentTimeoutsRef.current[message.id] = [
          ...(segmentTimeoutsRef.current[message.id] ?? []),
          ...newTimers,
        ];
      }
    });

    Object.keys(segmentCacheRef.current).forEach((messageId) => {
      if (activeIds.has(messageId)) return;

      const timers = segmentTimeoutsRef.current[messageId];
      if (timers) {
        timers.forEach((timeoutId) => clearTimeout(timeoutId));
        delete segmentTimeoutsRef.current[messageId];
      }

      delete segmentCacheRef.current[messageId];
    });

    setVisibleSegmentCounts((previous) => {
      let hasChanges = false;
      const nextState = { ...previous };

      Object.keys(nextState).forEach((messageId) => {
        if (!activeIds.has(messageId)) {
          delete nextState[messageId];
          hasChanges = true;
        }
      });

      Object.entries(nextVisibleCounts).forEach(([messageId, count]) => {
        if (nextState[messageId] !== count) {
          nextState[messageId] = count;
          hasChanges = true;
        }
      });

      return hasChanges ? nextState : previous;
    });
  }, [combinedMessages, customMessageContentById]);

  useLayoutEffect(() => {
    const activeMessageIds = new Set(combinedMessages.map((message) => message.id));

    combinedMessages.forEach((message) => {
      if (
        message.role !== 'assistant' ||
        message.isStreaming ||
        customMessageContentById.has(message.id) ||
        typeof message.content !== 'string' ||
        !message.content.trim() ||
        anchorScrollTopRef.current === null ||
        !message.shouldReveal ||
        progressivelyRevealedMessageIdsRef.current.has(message.id)
      ) {
        return;
      }

      const content = message.content;
      const contentLength = content.length;
      const firstVisibleCount = getNextRevealCount(content, 0);
      progressivelyRevealedMessageIdsRef.current.add(message.id);

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        announcedResponseMessageIdsRef.current.add(message.id);
        announceResponseReady();
        return;
      }

      setVisibleCharacterCounts((previous) => ({
        ...previous,
        [message.id]: firstVisibleCount,
      }));

      let revealedCount = firstVisibleCount;
      const revealNextChunk = () => {
        revealedCount = getNextRevealCount(content, revealedCount);
        setVisibleCharacterCounts((previous) => ({ ...previous, [message.id]: revealedCount }));

        if (revealedCount >= contentLength) {
          clearInterval(characterRevealTimeoutsRef.current[message.id]);
          delete characterRevealTimeoutsRef.current[message.id];
        }
      };

      if (firstVisibleCount < contentLength) {
        characterRevealTimeoutsRef.current[message.id] = setInterval(
          revealNextChunk,
          COMPLETED_RESPONSE_REVEAL_INTERVAL_MS
        );
      }
    });

    Object.keys(characterRevealTimeoutsRef.current).forEach((messageId) => {
      if (activeMessageIds.has(messageId)) return;

      clearInterval(characterRevealTimeoutsRef.current[messageId]);
      delete characterRevealTimeoutsRef.current[messageId];
      progressivelyRevealedMessageIdsRef.current.delete(messageId);
      announcedResponseMessageIdsRef.current.delete(messageId);
    });

    setVisibleCharacterCounts((previous) => {
      const staleMessageIds = Object.keys(previous).filter(
        (messageId) => !activeMessageIds.has(messageId)
      );
      if (!staleMessageIds.length) return previous;

      const next = { ...previous };
      staleMessageIds.forEach((messageId) => delete next[messageId]);
      return next;
    });
  }, [combinedMessages, customMessageContentById]);

  useEffect(() => {
    const previousMessageIds = previousCombinedMessageIdsRef.current;
    const nextMessageIds = combinedMessages.map((message) => message.id);
    previousCombinedMessageIdsRef.current = nextMessageIds;

    const announceAssistantResult = (messageId: string) => {
      announcedResponseMessageIdsRef.current.add(messageId);
      announceResponseReady();
    };

    combinedMessages.forEach((message) => {
      if (
        message.role !== 'assistant' ||
        typeof message.content !== 'string' ||
        !progressivelyRevealedMessageIdsRef.current.has(message.id) ||
        announcedResponseMessageIdsRef.current.has(message.id) ||
        visibleCharacterCounts[message.id] !== message.content.length
      ) {
        return;
      }

      announceAssistantResult(message.id);
    });

    const isAppendOnlyChange =
      previousMessageIds !== null &&
      nextMessageIds.length > previousMessageIds.length &&
      previousMessageIds.every((messageId, index) => nextMessageIds[index] === messageId);

    if (!isAppendOnlyChange || previousMessageIds.length === 0) return;

    combinedMessages.slice(previousMessageIds.length).forEach((message) => {
      if (
        message.role === 'assistant' &&
        !message.isStreaming &&
        !announcedResponseMessageIdsRef.current.has(message.id) &&
        !progressivelyRevealedMessageIdsRef.current.has(message.id)
      ) {
        announceAssistantResult(message.id);
      }
    });
  }, [combinedMessages, visibleCharacterCounts]);

  useEffect(() => {
    return () => {
      Object.values(segmentTimeoutsRef.current).forEach((timeouts) => {
        timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      });
      Object.values(characterRevealTimeoutsRef.current).forEach((timeoutId) =>
        clearInterval(timeoutId)
      );
      if (responseReadyAnnouncementTimeoutRef.current) {
        clearTimeout(responseReadyAnnouncementTimeoutRef.current);
      }
    };
  }, []);

  const messagesToRender = useMemo(() => {
    const shouldInsertStreamingPlaceholder = isAgentStreaming && !hasStreamingAssistantMessage;
    if (!shouldInsertStreamingPlaceholder) return combinedMessages;

    const streamingPlaceholder: ChatMessage = {
      content: '',
      id: 'assistant-streaming-placeholder',
      isStreaming: true,
      role: 'assistant',
      variant: 'status',
    };

    return [...combinedMessages, streamingPlaceholder];
  }, [combinedMessages, hasStreamingAssistantMessage, isAgentStreaming]);

  const showStreamingSkeleton = isAgentStreaming && !hasStreamingAssistantMessage;
  const shouldShowResponseProgress = messagesToRender.some(
    (message) =>
      !customMessageContentById.has(message.id) &&
      message.role === 'assistant' &&
      Boolean(message.isStreaming) &&
      isAgentStreaming
  );
  const shouldShowInputLoader = awaitingResponse || showStreamingSkeleton;

  const isSubmitDisabled = inputDisabled || submitDisabled || awaitingResponse;
  const hasInputText = inputValue.trim().length > 0;
  const inputDisplayValue = awaitingResponse ? (pendingInput ?? inputValue) : inputValue;
  const isTextAreaDisabled = inputDisabled || awaitingResponse;

  useEffect(() => {
    const clearResponseProgressTimeouts = () => {
      responseProgressTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      responseProgressTimeoutsRef.current = [];
    };

    if (!shouldShowResponseProgress) {
      clearResponseProgressTimeouts();
      setIsResponseProgressFading(false);
      return undefined;
    }

    const getNextMessage = () => {
      if (!responseProgressQueueRef.current.length) {
        responseProgressQueueRef.current = shuffleResponseProgressMessages(
          responseProgressLastMessageRef.current
        );
      }

      const nextMessage = responseProgressQueueRef.current.shift() ?? RESPONSE_PROGRESS_MESSAGES[0];
      responseProgressLastMessageRef.current = nextMessage;
      return nextMessage;
    };

    const scheduleNextMessage = () => {
      const delay =
        RESPONSE_PROGRESS_MIN_DELAY_MS +
        Math.random() * (RESPONSE_PROGRESS_MAX_DELAY_MS - RESPONSE_PROGRESS_MIN_DELAY_MS);
      const timeoutId = setTimeout(() => {
        const isReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        if (isReducedMotion) {
          setResponseProgressMessage(getNextMessage());
          scheduleNextMessage();
          return;
        }

        setIsResponseProgressFading(true);

        const fadeTimeoutId = setTimeout(() => {
          setResponseProgressMessage(getNextMessage());
          setIsResponseProgressFading(false);
          scheduleNextMessage();
        }, RESPONSE_PROGRESS_FADE_DURATION_MS);

        responseProgressTimeoutsRef.current.push(fadeTimeoutId);
      }, delay);

      responseProgressTimeoutsRef.current.push(timeoutId);
    };

    responseProgressQueueRef.current = [];
    responseProgressLastMessageRef.current = null;
    setResponseProgressMessage(getNextMessage());
    scheduleNextMessage();

    return clearResponseProgressTimeouts;
  }, [shouldShowResponseProgress]);

  const shouldRenderMarkdownForRole = (role: ChatRole) => {
    if (renderMarkdown === 'none') return false;
    if (renderMarkdown === 'all') return true;
    return role === 'assistant';
  };

  useEffect(() => {
    if (!messages.length) {
      setLocalMessages([]);
    }
  }, [messages]);

  useEffect(() => {
    if (!awaitingResponse && localMessages.length) {
      setLocalMessages([]);
    }
  }, [awaitingResponse, localMessages.length]);

  useLayoutEffect(() => {
    if (!autoScroll || !isAgentStreaming) return;

    const latestUserMessage = [...combinedMessages]
      .reverse()
      .find((message) => message.role === 'user');
    if (!latestUserMessage || lastAnchoredUserMessageIdRef.current === latestUserMessage.id) return;

    if (anchorScrollTopRef.current === null) {
      hasUserMovedViewportRef.current = false;
    }

    hasResponseReservationRef.current = true;
    lastAnchoredUserMessageIdRef.current = latestUserMessage.id;
    pendingScrollAnchorIdRef.current = latestUserMessage.id;

    const container = scrollContainerRef.current;
    if (container) {
      setResponseReservationHeight(
        Math.max(0, container.clientHeight - USER_MESSAGE_BOTTOM_OFFSET_PX)
      );
    }
  }, [autoScroll, combinedMessages, isAgentStreaming]);

  useLayoutEffect(() => {
    if (!hasResponseReservationRef.current) return undefined;

    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const updateResponseReservationHeight = () => {
      if (!hasResponseReservationRef.current) return;

      const anchorScrollTop = anchorScrollTopRef.current;
      const reservation = responseReservationRef.current;

      if (
        anchorScrollTop !== null &&
        reservation &&
        !awaitingResponse &&
        !isAgentStreaming &&
        !hasCompletedResponseRevealInProgress
      ) {
        const containerRect = container.getBoundingClientRect();
        const reservationRect = reservation.getBoundingClientRect();
        const naturalContentEnd = reservationRect.top - containerRect.top + container.scrollTop;

        setResponseReservationHeight(
          Math.max(0, anchorScrollTop + container.clientHeight - naturalContentEnd)
        );
        return;
      }

      setResponseReservationHeight(
        Math.max(0, container.clientHeight - USER_MESSAGE_BOTTOM_OFFSET_PX)
      );
    };

    updateResponseReservationHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const resizeObserver = new ResizeObserver(updateResponseReservationHeight);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [awaitingResponse, hasCompletedResponseRevealInProgress, isAgentStreaming]);

  useLayoutEffect(() => {
    if (!autoScroll) return;

    const container = scrollContainerRef.current;
    const anchorId = pendingScrollAnchorIdRef.current;
    const anchorBubble = anchorId ? messageBubbleRefs.current[anchorId] : null;
    if (!container || !anchorBubble) return;

    if (anchorScrollTopRef.current !== null && hasUserMovedViewportRef.current) {
      pendingScrollAnchorIdRef.current = null;
      return;
    }

    const expectedReservationHeight = Math.max(
      0,
      container.clientHeight - USER_MESSAGE_BOTTOM_OFFSET_PX
    );
    if (responseReservationHeight !== expectedReservationHeight) return;

    const containerRect = container.getBoundingClientRect();
    const anchorBubbleRect = anchorBubble.getBoundingClientRect();
    const anchorBottomInScrollContent =
      anchorBubbleRect.bottom - containerRect.top + container.scrollTop;

    scrollChatBodyTo(
      container,
      Math.max(0, anchorBottomInScrollContent - USER_MESSAGE_BOTTOM_OFFSET_PX)
    );
    anchorScrollTopRef.current = container.scrollTop;
    pendingScrollAnchorIdRef.current = null;
  }, [autoScroll, combinedMessages, localMessages, responseReservationHeight]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const anchorScrollTop = anchorScrollTopRef.current;
    if (
      !container ||
      anchorScrollTop === null ||
      awaitingResponse ||
      isAgentStreaming ||
      hasCompletedResponseRevealInProgress
    ) {
      return;
    }

    const reservation = responseReservationRef.current;
    if (!reservation) return;

    const containerRect = container.getBoundingClientRect();
    const reservationRect = reservation.getBoundingClientRect();
    const naturalContentEnd = reservationRect.top - containerRect.top + container.scrollTop;
    const remainingReservationHeight = Math.max(
      0,
      anchorScrollTop + container.clientHeight - naturalContentEnd
    );

    setResponseReservationHeight(remainingReservationHeight);
  }, [awaitingResponse, combinedMessages, hasCompletedResponseRevealInProgress, isAgentStreaming]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const anchorScrollTop = anchorScrollTopRef.current;
    if (
      !container ||
      anchorScrollTop === null ||
      awaitingResponse ||
      isAgentStreaming ||
      hasUserMovedViewportRef.current
    ) {
      return;
    }

    scrollChatBodyTo(
      container,
      Math.min(anchorScrollTop, container.scrollHeight - container.clientHeight)
    );
  }, [awaitingResponse, combinedMessages, isAgentStreaming, responseReservationHeight]);

  useEffect(() => {
    const prevAssistantCount = assistantCountSnapshotRef.current;
    const prevIsAgentStreaming = isAgentStreamingSnapshotRef.current;

    const assistantArrived = awaitingResponse && assistantMessageCount > prevAssistantCount;
    const agentFinished = awaitingResponse && prevIsAgentStreaming && !isAgentStreaming;

    if (assistantArrived || agentFinished) {
      setAwaitingResponse(false);
      setPendingInput(null);
    }

    assistantCountSnapshotRef.current = assistantMessageCount;
    isAgentStreamingSnapshotRef.current = isAgentStreaming;
  }, [assistantMessageCount, awaitingResponse, isAgentStreaming]);

  const handleChangeInput = (value: string) => onChangeInput?.(value);

  const handleChatBodyScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const previousScrollTop = lastScrollTopRef.current;
    const nextScrollTop = container.scrollTop;
    const programmaticScrollTop = programmaticScrollTopRef.current;

    lastScrollTopRef.current = nextScrollTop;
    programmaticScrollTopRef.current = null;

    if (
      nextScrollTop === previousScrollTop ||
      (programmaticScrollTop !== null &&
        Math.abs(nextScrollTop - programmaticScrollTop) <= SCROLL_POSITION_TOLERANCE_PX)
    ) {
      return;
    }

    hasUserMovedViewportRef.current = true;

    const anchorScrollTop = anchorScrollTopRef.current;
    if (
      anchorScrollTop === null ||
      awaitingResponse ||
      isAgentStreaming ||
      nextScrollTop >= previousScrollTop
    ) {
      return;
    }

    const contentEndScrollTop = Math.max(
      0,
      container.scrollHeight - responseReservationHeight - container.clientHeight
    );
    if (nextScrollTop > contentEndScrollTop) return;

    anchorScrollTopRef.current = null;
    hasResponseReservationRef.current = false;
    setResponseReservationHeight(0);
  };

  const completeInProgressResponseReveals = () => {
    const revealingMessageIds = Object.keys(characterRevealTimeoutsRef.current);
    if (!revealingMessageIds.length) return;

    revealingMessageIds.forEach((messageId) => {
      clearInterval(characterRevealTimeoutsRef.current[messageId]);
      delete characterRevealTimeoutsRef.current[messageId];
      announcedResponseMessageIdsRef.current.add(messageId);
    });

    setVisibleCharacterCounts((previous) => {
      const next = { ...previous };

      revealingMessageIds.forEach((messageId) => {
        const message = combinedMessages.find((candidate) => candidate.id === messageId);
        if (typeof message?.content === 'string') next[messageId] = message.content.length;
      });

      return next;
    });
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    completeInProgressResponseReveals();

    const nextMessage: ChatMessage = {
      content: trimmedValue,
      id: `local-user-${Date.now()}-${generatedMessageCounter.current}`,
      role: 'user',
    };
    generatedMessageCounter.current += 1;

    assistantCountSnapshotRef.current = assistantMessageCount;
    isAgentStreamingSnapshotRef.current = isAgentStreaming;

    setPendingInput(trimmedValue);
    setResponseReadyAnnouncement('');
    setAwaitingResponse(true);
    remoteMessageIdsAtSubmitRef.current = new Set(messages.map((message) => message.id));
    hasResponseReservationRef.current = true;
    hasUserMovedViewportRef.current = false;
    lastAnchoredUserMessageIdRef.current = nextMessage.id;
    pendingScrollAnchorIdRef.current = nextMessage.id;
    setLocalMessages((previousMessages) => [...previousMessages, nextMessage]);

    onSubmit?.(trimmedValue);
    onChangeInput?.('');
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    handleSubmit();
  };

  return (
    <div className={classNames(styles['chat-window'], className)}>
      {headerTitle && (
        <div className={styles['chat-header']} data-testid="chat-header">
          <h3> {headerTitle} </h3>
          {headerAction && <div className={styles['chat-header-action']}>{headerAction}</div>}
        </div>
      )}
      <div
        className={styles['chat-body']}
        data-testid="chat-body"
        onScroll={handleChatBodyScroll}
        ref={scrollContainerRef}
      >
        <ScreenReaderOnly aria-live="polite" data-testid="chat-response-ready-announcement">
          {responseReadyAnnouncement}
        </ScreenReaderOnly>
        <div className={styles['chat-messages']}>
          <div className={styles['message-list']}>
            {messagesToRender.map((message) => {
              const hasCustomContent = customMessageContentById.has(message.id);
              const customContent = customMessageContentById.get(message.id);
              const isStatusMessage = message.variant === 'status';
              const shouldShowMessageLoader =
                !hasCustomContent &&
                message.role === 'assistant' &&
                Boolean(message.isStreaming) &&
                isAgentStreaming;
              const isStringContent = typeof message.content === 'string';
              const contentString = isStringContent ? (message.content as string) : '';
              const segments =
                isStringContent && !hasCustomContent
                  ? (segmentCacheRef.current[message.id] ??
                    splitAssistantMessageSegments(contentString))
                  : null;

              if (isStringContent && segments && !segmentCacheRef.current[message.id]) {
                segmentCacheRef.current[message.id] = segments;
              }

              const totalSegments = segments?.length ?? 0;
              const visibleSegmentCount =
                segments && totalSegments > 0
                  ? (visibleSegmentCounts[message.id] ?? totalSegments)
                  : null;
              const hasPendingSegments =
                segments && visibleSegmentCount !== null && visibleSegmentCount < totalSegments;
              const visibleContent =
                segments && visibleSegmentCount !== null
                  ? segments.slice(0, visibleSegmentCount).join('\n\n')
                  : isStringContent
                    ? contentString
                    : null;
              const contentToRender =
                typeof visibleCharacterCounts[message.id] === 'number' && isStringContent
                  ? contentString.slice(0, visibleCharacterCounts[message.id])
                  : typeof visibleContent === 'string'
                    ? visibleContent
                    : message.content;
              const isCompletedResponseRevealing =
                typeof visibleCharacterCounts[message.id] === 'number' &&
                isStringContent &&
                visibleCharacterCounts[message.id] < contentString.length;

              return (
                <ChatMessageItem
                  contentToRender={contentToRender}
                  customContent={customContent}
                  hasCustomContent={hasCustomContent}
                  hasPendingSegments={Boolean(hasPendingSegments)}
                  isCompletedResponseRevealing={isCompletedResponseRevealing}
                  isStatusMessage={isStatusMessage}
                  key={message.id}
                  message={message}
                  onBubbleRef={(node) => {
                    if (node) {
                      messageBubbleRefs.current[message.id] = node;
                      return;
                    }

                    delete messageBubbleRefs.current[message.id];
                  }}
                  renderMarkdown={shouldRenderMarkdownForRole(message.role)}
                  responseProgress={
                    shouldShowMessageLoader ? (
                      <ResponseProgress
                        isFading={isResponseProgressFading}
                        message={responseProgressMessage}
                      />
                    ) : undefined
                  }
                />
              );
            })}
            <div
              aria-hidden="true"
              className={styles['response-reservation']}
              data-testid="chat-response-reservation"
              ref={responseReservationRef}
              style={{ height: responseReservationHeight }}
            />
          </div>
        </div>
      </div>

      <div className={styles['chat-footer']}>
        {footerHint && <div className={styles['chat-footer-hint']}>{footerHint}</div>}

        <ChatInput
          errorMessageId={inputErrorMessageId}
          hasInputText={hasInputText}
          isDisabled={isTextAreaDisabled}
          isInvalid={inputHasError}
          isLoading={shouldShowInputLoader}
          isSubmitDisabled={isSubmitDisabled}
          onChange={handleChangeInput}
          onKeyDown={handleInputKeyDown}
          onSubmit={handleSubmit}
          value={inputDisplayValue}
        />
        {inputFooter && <div className={styles['chat-input-footer']}>{inputFooter}</div>}
      </div>
    </div>
  );
};

export default ChatWindow;
